// Social posts, stored on the server (docs/SOCIAL_BACKEND.md §1:
// /api/v2/social/posts) so a post planned on the salon's laptop shows on
// their phone. A post is its design (type, layout, look, content, photos),
// its caption, and when it goes out.
//
// status: 'draft' | 'planned' | 'posted'
// plannedAt / postedAt: ISO strings; thumb: a small JPEG (an https address
// once uploaded; a data URL only until then, and only in this browser)
//
// The public interface is the one the components have always used, and it
// stays synchronous: localStorage is the cache (and the offline copy), reads
// come from it, writes land in it at once and go to the server behind, and
// subscribers hear about both. The first read on a page pulls the server's
// list into the cache. A write the server hasn't confirmed is kept
// (`_dirty`) and sent again on the next pull, so nothing made offline is lost.
//
// Ids are Mongo ObjectIds minted here, so a post has one id from its first
// autosave and a retried create can't make a second copy. Posts from before
// the server (ids like post-…) are given one and sent up once, their
// thumbnails uploaded on the way.
import { listPosts, createPost, updatePost, removePost, restorePostApi, uploadDataUrl } from './postsApi'
import { POST_TYPES, upgradePost } from './postTypes'

const salonKey = () => localStorage.getItem('salonId') || 'me'
const userKey = () => localStorage.getItem('userId') || 'me'
const LEGACY_KEY = () => `beu-social-posts-${salonKey()}`
const KEY = () => `beu-social-posts-${salonKey()}-${userKey()}`
const GONE_KEY = () => `beu-social-posts-deleted-${salonKey()}-${userKey()}`
const EVENT = 'beu-social-posts'
const MAX = 200
const PULL_EVERY = 60 * 1000

const OID = /^[0-9a-f]{24}$/i
export const isServerPostId = (id) => typeof id === 'string' && OID.test(id)
const hex = (n) => {
  let s = ''
  const bytes = new Uint8Array(n)
  try { window.crypto.getRandomValues(bytes) } catch (e) { for (let i = 0; i < n; i++) bytes[i] = Math.floor(Math.random() * 256) }
  bytes.forEach((b) => { s += b.toString(16).padStart(2, '0') })
  return s
}
export const newPostId = () => Math.floor(Date.now() / 1000).toString(16).padStart(8, '0') + hex(8)

const online = () => !!localStorage.getItem('accessToken')
const isData = (s) => typeof s === 'string' && s.indexOf('data:') === 0

const readJson = (key, fallback) => {
  try {
    const raw = JSON.parse(localStorage.getItem(key) || 'null')
    return raw == null ? fallback : raw
  } catch (e) {
    return fallback
  }
}

const notify = () => { try { window.dispatchEvent(new CustomEvent(EVENT)) } catch (e) { /* old browsers */ } }

const byEdited = (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)

const persist = (list) => {
  const sorted = list.slice().sort(byEdited).slice(0, MAX)
  try {
    localStorage.setItem(KEY(), JSON.stringify(sorted))
  } catch (e) {
    // Out of room: drop local-only thumbnails from the oldest posts and try once more
    try { localStorage.setItem(KEY(), JSON.stringify(sorted.map((p, i) => (i > 40 && isData(p.thumb) ? { ...p, thumb: null } : p)))) } catch (x) { /* private mode */ }
  }
  notify()
  return sorted
}

// Posts kept by this browser before the server stored them move into this
// user's cache once, with new ids, marked to be sent up.
const adoptLegacy = () => {
  const legacy = readJson(LEGACY_KEY(), null)
  if (!Array.isArray(legacy)) return null
  const mine = readJson(KEY(), [])
  // Posts from before designs had families (or an old type) are brought up
  // to date first, so the server gets a post it can store.
  const current = (p) => (p.family && POST_TYPES[p.type] ? p : upgradePost({ ...p, family: undefined }))
  const adopted = legacy.filter(p => p && p.id && p.type).map(p => ({ ...current(p), id: isServerPostId(p.id) ? p.id : newPostId(), _dirty: true }))
  const next = [...(Array.isArray(mine) ? mine : []), ...adopted]
  try {
    localStorage.setItem(KEY(), JSON.stringify(next))
    localStorage.removeItem(LEGACY_KEY())
  } catch (e) { return null }
  return next
}

let lastPull = 0
let pulling = null

export const loadPosts = () => {
  const raw = adoptLegacy() || readJson(KEY(), [])
  if (online() && Date.now() - lastPull > PULL_EVERY) pullPosts()
  return (Array.isArray(raw) ? raw : []).filter(p => p && p.id && p.type)
}

// ─── Server sync ───

const gone = () => readJson(GONE_KEY(), {}) || {}
const setGone = (map) => { try { localStorage.setItem(GONE_KEY(), JSON.stringify(map)) } catch (e) { /* ignore */ } }

const confirmed = new Set() // ids the server has
const writes = {} // id -> { latest, running }
let refusedOnce = false
let localOnlyReason = null
// Why posts are being kept in this browser only ('managed' | 'no_access'), or
// null while the server is taking them.
export const keptHereOnly = () => localOnlyReason

// What the server stores: no local-only fields, no images inline.
const uploadedUrls = new Map()
const upload = async (dataUrl) => {
  if (uploadedUrls.has(dataUrl)) return uploadedUrls.get(dataUrl)
  const url = await uploadDataUrl(dataUrl)
  uploadedUrls.set(dataUrl, url)
  return url
}
const forServer = async (post) => {
  const { _dirty, fromBailey, createdAt, updatedAt, _id, ...rest } = post
  const out = { ...rest }
  if (isData(out.thumb)) {
    try { out.thumb = await upload(out.thumb) } catch (e) { out.thumb = '' }
  } else if (out.thumb && !/^https:\/\//.test(out.thumb)) out.thumb = ''
  if (out.thumb == null) out.thumb = ''
  if (out.photos) {
    const photos = {}
    for (const slot of Object.keys(out.photos)) {
      const ph = out.photos[slot]
      if (ph && isData(ph.src)) {
        try { photos[slot] = { ...ph, src: await upload(ph.src) } } catch (e) { photos[slot] = { ...ph, src: '' } }
      } else photos[slot] = ph
    }
    out.photos = photos
  }
  return out
}

// Swap what came back into the cache, unless the post has changed again since.
const settle = (id, sent, server) => {
  const list = readJson(KEY(), [])
  const i = list.findIndex(p => p.id === id)
  if (i < 0) return
  const cur = list[i]
  if (cur.updatedAt !== sent.updatedAt) return
  const next = { ...cur, _dirty: false }
  delete next._dirty
  if (server && server.thumb && isData(cur.thumb)) next.thumb = server.thumb
  if (server && server.photos) next.photos = { ...cur.photos, ...server.photos }
  list[i] = next
  persist(list)
}

const refused = (e) => {
  // The server won't take posts from this person (salon marketing is the
  // owner's for now, or the salon runs this stylist's marketing): they stay
  // in this browser. Say so once in the console, never in a loop.
  if (!refusedOnce) {
    refusedOnce = true
    console.warn('Social posts kept in this browser only:', e && e.message)
    // The page says so too (keptHereOnly): a post that only lives in one
    // browser is lost with it, and nobody else at the salon sees it.
    localOnlyReason = e && e.reason === 'salon_runs_marketing' ? 'managed' : 'no_access'
    notify()
  }
}
const isRefusal = (e) => e && (e.code === 403 || e.reason === 'salon_runs_marketing')

const runWrite = async (id) => {
  const w = writes[id]
  if (!w || w.running) return
  w.running = true
  while (w.latest) {
    const job = w.latest
    w.latest = null
    try {
      if (job.kind === 'save') {
        const body = await forServer(job.post)
        // Nothing new since the last write that went up: don't send it again.
        // "I've posted it" saved the same post three times (the share sheet,
        // markPosted, then the editor's autosave of what came back).
        const { updatedAt, createdAt, ...same } = body
        const sig = JSON.stringify(same)
        if (w.sentSig === sig && confirmed.has(id)) { settle(id, job.post, null); continue } // eslint-disable-line no-continue
        const server = confirmed.has(id)
          ? await updatePost(id, body).catch((e) => { if (/not found/i.test(e.message)) { confirmed.delete(id); return createPost({ ...body, id }) } throw e })
          : await createPost({ ...body, id })
        confirmed.add(id)
        w.sentSig = sig
        settle(id, job.post, server)
      } else if (job.kind === 'delete') {
        if (isServerPostId(id)) await removePost(id).catch((e) => { if (!/not found/i.test(e.message)) throw e })
        const g = gone(); delete g[id]; setGone(g)
      } else if (job.kind === 'restore') {
        await restorePostApi(id).catch(async (e) => {
          if (!/not found/i.test(e.message)) throw e
          await createPost({ ...(await forServer(job.post)), id }) // never reached the server
        })
        confirmed.add(id)
        settle(id, job.post, null)
      }
    } catch (e) {
      if (isRefusal(e)) refused(e)
      // Kept as _dirty (or in the deleted list) and tried again on the next pull.
    }
  }
  w.running = false
}

const queue = (id, job) => {
  if (!online()) return
  writes[id] = writes[id] || { latest: null, running: false }
  writes[id].latest = job
  runWrite(id)
}

/** Pull the server's posts into the cache, then send anything not yet confirmed. */
export const pullPosts = () => {
  if (!online()) return Promise.resolve(loadLocal())
  if (pulling) return pulling
  lastPull = Date.now()
  const key = KEY()
  pulling = listPosts({ limit: 500 })
    .then((res) => {
      if (key !== KEY()) return loadLocal() // switched salon or account meanwhile
      const server = (res.posts || []).map(({ _id, isRemoved, removedAt, removedBy, salonId, ownerUserId, createdBy, updatedBy, ...p }) => ({ ...p, id: String(p.id || _id) }))
      server.forEach(p => confirmed.add(p.id))
      const deleted = gone()
      const local = loadLocal()
      const pending = local.filter(p => p._dirty)
      const pendingIds = new Set(pending.map(p => p.id))
      // Keep a local-only thumbnail until its upload lands
      const localThumbs = new Map(local.filter(p => isData(p.thumb)).map(p => [p.id, p.thumb]))
      const merged = [
        ...pending.filter(p => !deleted[p.id]),
        ...server.filter(p => !pendingIds.has(p.id) && !deleted[p.id]).map(p => (p.thumb || !localThumbs.has(p.id) ? p : { ...p, thumb: localThumbs.get(p.id) })),
      ]
      const list = persist(merged)
      pending.filter(p => !deleted[p.id]).forEach(p => queue(p.id, { kind: 'save', post: p }))
      Object.keys(deleted).forEach(id => queue(id, { kind: 'delete' }))
      return list
    })
    .catch((e) => {
      if (isRefusal(e)) refused(e)
      return loadLocal()
    })
    .finally(() => { pulling = null })
  return pulling
}

const loadLocal = () => {
  const raw = readJson(KEY(), [])
  return (Array.isArray(raw) ? raw : []).filter(p => p && p.id && p.type)
}

// ─── Writes (synchronous for the caller; the server follows) ───

export const savePost = (post) => {
  const now = new Date().toISOString()
  const id = isServerPostId(post.id) ? post.id : newPostId()
  const { fromBailey, ...clean } = post
  const next = { ...clean, id, updatedAt: now, createdAt: post.createdAt || now, _dirty: true }
  const rest = loadLocal().filter(p => p.id !== id && p.id !== post.id)
  persist([next, ...rest])
  queue(id, { kind: 'save', post: next })
  return next
}

export const deletePost = (id) => {
  const list = persist(loadLocal().filter(p => p.id !== id))
  if (isServerPostId(id)) {
    setGone({ ...gone(), [id]: Date.now() })
    queue(id, { kind: 'delete' })
  }
  return list
}

// Undo a delete: the post comes back exactly as it was, in its place
export const restorePost = (post) => {
  if (!post || !post.id) return null
  const list = [post, ...loadLocal().filter(p => p.id !== post.id)]
  persist(list)
  if (isServerPostId(post.id)) {
    const g = gone(); delete g[post.id]; setGone(g)
    queue(post.id, { kind: 'restore', post })
  }
  return post
}

export const markPosted = (id) => {
  const post = loadLocal().find(p => p.id === id)
  if (!post) return null
  return savePost({ ...post, status: 'posted', postedAt: new Date().toISOString() })
}

export const subscribePosts = (fn) => {
  const on = () => fn()
  const onStorage = (e) => { if (e.key && e.key.indexOf('beu-social-posts-') === 0) fn() }
  const onOnline = () => { lastPull = 0; pullPosts() }
  window.addEventListener(EVENT, on)
  window.addEventListener('storage', onStorage)
  window.addEventListener('online', onOnline)
  return () => {
    window.removeEventListener(EVENT, on)
    window.removeEventListener('storage', onStorage)
    window.removeEventListener('online', onOnline)
  }
}

// Things Bailey has already turned into posts (a review shared once isn't
// suggested again): anything the salon has posted, on any device (the
// server keeps each post's `source`), plus this browser's own marks.
const USED_KEY = () => `beu-social-used-${salonKey()}`
export const usedSources = () => {
  let local = {}
  try { local = JSON.parse(localStorage.getItem(USED_KEY()) || '{}') || {} } catch (e) { local = {} }
  const fromPosts = {}
  loadLocal().forEach((p) => {
    if (p.source && p.status === 'posted') fromPosts[p.source] = new Date(p.postedAt || p.updatedAt || 0).getTime()
  })
  return { ...fromPosts, ...local }
}
export const markSourceUsed = (key) => {
  if (!key) return
  try {
    const local = JSON.parse(localStorage.getItem(USED_KEY()) || '{}') || {}
    localStorage.setItem(USED_KEY(), JSON.stringify({ ...local, [key]: Date.now() }))
  } catch (e) { /* ignore */ }
}

// Due: planned for now or earlier today and not yet posted
export const isDue = (p, now = new Date()) => p.status === 'planned' && p.plannedAt && new Date(p.plannedAt) <= now
export const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

// For tests
export const __resetSync = () => { confirmed.clear(); Object.keys(writes).forEach(k => delete writes[k]); lastPull = 0; pulling = null; refusedOnce = false; localOnlyReason = null; uploadedUrls.clear() }

// ─── Favourites ───
// Designs the salon uses again and again (their before and after, their
// review post): the kind of post, its style, colours, fonts and format, and
// their own wording, but never facts like prices, names or times. Kept per
// salon in this browser until the backend stores them (docs/SOCIAL_BACKEND.md).
const FAV_KEY = () => `beu-social-favourites-${localStorage.getItem('salonId') || 'me'}`
const FAV_EVENT = 'beu-social-favourites'
const FAV_MAX = 24

export const loadFavourites = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(FAV_KEY()) || '[]')
    return Array.isArray(raw) ? raw.filter(f => f && f.id && f.type && f.family) : []
  } catch (e) {
    return []
  }
}
const keepFavourites = (list) => {
  try { localStorage.setItem(FAV_KEY(), JSON.stringify(list.slice(0, FAV_MAX))) } catch (e) { /* private mode */ }
  try { window.dispatchEvent(new CustomEvent(FAV_EVENT)) } catch (e) { /* old browsers */ }
  return list
}
// Two favourites are the same when they'd make the same post
export const favouriteKey = (f) => [f.type, f.family, f.family === 'layouts' ? f.layout || '' : '', f.colourway, f.format || 'post', JSON.stringify(f.tune || {}), JSON.stringify(f.words || {})].join('|')
export const findFavourite = (fav) => loadFavourites().find(f => favouriteKey(f) === favouriteKey(fav)) || null
export const addFavourite = (fav) => {
  const found = findFavourite(fav)
  if (found) return found
  const next = { ...fav, id: `fav-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`, createdAt: new Date().toISOString() }
  keepFavourites([next, ...loadFavourites()])
  return next
}
export const removeFavourite = (id) => keepFavourites(loadFavourites().filter(f => f.id !== id))
// Undo a removal: back in its place
export const restoreFavourite = (fav, index = 0) => {
  const list = loadFavourites().filter(f => f.id !== fav.id)
  list.splice(Math.max(0, Math.min(index, list.length)), 0, fav)
  return keepFavourites(list)
}
// Used most recently first
export const touchFavourite = (id) => {
  const list = loadFavourites()
  const f = list.find(x => x.id === id)
  if (!f) return
  keepFavourites([{ ...f, usedAt: new Date().toISOString() }, ...list.filter(x => x.id !== id)])
}
export const subscribeFavourites = (fn) => {
  const on = () => fn()
  const onStorage = (e) => { if (e.key && e.key.indexOf('beu-social-favourites-') === 0) fn() }
  window.addEventListener(FAV_EVENT, on)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(FAV_EVENT, on)
    window.removeEventListener('storage', onStorage)
  }
}
